import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";

// ---------------------------------------------------------------------------
// Generic queued action — the only type the engine knows about
// ---------------------------------------------------------------------------

export type QueuedAction = {
	id: string;
	execute: () => Promise<void>;
	rollback: (() => void) | null;
	toast: {
		/** String for default toast.loading(), or function for toast.custom() */
		loading: string | (() => React.ReactElement);
		success: string;
		error: string | ((err: unknown) => string);
	};
	onSuccess?: () => void;
	onError?: () => void;
};

// ---------------------------------------------------------------------------
// Queue engine — knows nothing about entries, endpoints, or action kinds
// ---------------------------------------------------------------------------

const LOADING_DELAY_MS = 200;

export class ActionQueue {
	private queue: QueuedAction[] = [];
	private processing = false;
	private onQueueSizeChange: (size: number) => void;

	constructor(onQueueSizeChange: (size: number) => void) {
		this.onQueueSizeChange = onQueueSizeChange;
	}

	enqueue(action: QueuedAction) {
		this.queue.push(action);
		this.onQueueSizeChange(this.queue.length);
		if (!this.processing) this.processNext();
	}

	private async processNext() {
		if (this.queue.length === 0) {
			this.processing = false;
			return;
		}

		this.processing = true;
		const action = this.queue[0];

		const isCustomLoading = typeof action.toast.loading !== "string";

		try {
			await this.run(action);
			action.onSuccess?.();
		} catch (e) {
			action.rollback?.();
			if (isCustomLoading) toast.dismiss(action.id);
			toast.error(typeof action.toast.error === "function" ? action.toast.error(e) : action.toast.error, {
				...(isCustomLoading ? {} : { id: action.id }),
				duration: TOAST_DURATION.error,
				description: e instanceof Error ? e.message : undefined,
			});
			action.onError?.();
		}

		this.queue.shift();
		this.onQueueSizeChange(this.queue.length);
		this.processNext();
	}

	private run(action: QueuedAction): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			let settled = false;

			const loadingTimer = setTimeout(() => {
				if (!settled) {
					this.showLoading(action);
				}
			}, LOADING_DELAY_MS);

			const isCustomLoading = typeof action.toast.loading !== "string";

			action
				.execute()
				.then(() => {
					settled = true;
					clearTimeout(loadingTimer);
					if (isCustomLoading) {
						// Custom toasts don't cleanly transition to success — dismiss first
						toast.dismiss(action.id);
						toast.success(action.toast.success, {
							duration: TOAST_DURATION.success,
						});
					} else {
						toast.success(action.toast.success, {
							id: action.id,
							duration: TOAST_DURATION.success,
						});
					}
					resolve();
				})
				.catch((err) => {
					settled = true;
					clearTimeout(loadingTimer);
					reject(err);
				});
		});
	}

	private showLoading(action: QueuedAction) {
		const { loading } = action.toast;
		if (typeof loading === "string") {
			toast.loading(loading, { id: action.id });
		} else {
			toast.custom(loading, { id: action.id, duration: Infinity, position: "top-center" });
		}
	}
}

// ---------------------------------------------------------------------------
// Helper to build a QueuedAction with auto-generated id
// ---------------------------------------------------------------------------

export function createAction(
	action: Omit<QueuedAction, "id">,
): QueuedAction {
	return { ...action, id: crypto.randomUUID() };
}
